// Manual check-in modal for all open symptoms (OPEN_SYMPTOMS_CHECKIN_PLAN.md Task 3).
// Stacks the existing SymptomFollowUpCard for every open symptom, due or not.

import type { OpenSymptom, SymptomCardDensity } from "@medassist/shared";
import { ModalShell } from "./ModalShell.js";
import { SymptomFollowUpCard } from "./SymptomFollowUpCard.js";
import { BoltIcon } from "./icons.js";

interface Props {
  patientId: string;
  symptoms: OpenSymptom[];               // live list from DashboardPane state
  density: SymptomCardDensity;           // resolved by the widget (D-OSC-3)
  onDone: (key: string) => void;         // bubbles to onOpenSymptomDone
  onClose: () => void;
}

function snoozedUntilLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function OpenSymptomsCheckInModal({ patientId, symptoms, density, onDone, onClose }: Props) {
  return (
    <ModalShell
      title="Check in on symptoms"
      icon={<BoltIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      size="lg"
      bodyClassName="overflow-y-auto p-4 space-y-3"
    >
      {symptoms.length === 0 ? (
        <div className="space-y-4 py-4 text-center">
          <p className="text-sm text-gray-700 dark:text-gray-200">✓ All caught up — nothing open right now.</p>
          <button
            onClick={onClose}
            className="rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
          >
            Close
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Every symptom still being tracked — update or resolve any of them now; no need to wait for a reminder.
          </p>
          {symptoms.map((s) => (
            <div key={s.key}>
              {s.snoozedUntil && Date.parse(s.snoozedUntil) > Date.now() && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Reminders snoozed until {snoozedUntilLabel(s.snoozedUntil)}
                </p>
              )}
              <SymptomFollowUpCard
                patientId={patientId}
                symptom={s}
                density={density}
                hideSnooze
                onDone={onDone}
                siteId={s.siteId}
              />
            </div>
          ))}
        </>
      )}
    </ModalShell>
  );
}
